import React, { useEffect, useState } from "react";
import { formatDate } from "../utils/DateFormater";
import { Link } from "react-router-dom";
import { Col, Row, Spinner, Button, Form, Alert } from "reactstrap";
import Layout, { Main } from "../Layout";
import styled from "styled-components";
import useCelebritiesStore from "../store/CelebritiesStore";
import { mediaQueries } from "../theme";
import {
  borderColor,
  subtleBg,
  CountBadge,
  DATE_ACCESSOR,
  EmptyState,
  OrderStatus,
  Section,
  SectionHeader,
  SortableHeaders,
  STATUS_ACCESSOR,
  StyledTable,
  TableWrapper,
  TEXT_ACCESSOR,
  useTableSort,
} from "../component/DataTable";

const FilterForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0.9rem;
  margin-bottom: 1rem;
  border: 1px solid ${borderColor};
  border-radius: 12px;
  background-color: ${subtleBg};
`;

const FilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${mediaQueries.tablet} {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
`;

const FilterLegend = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.6;
  white-space: nowrap;

  ${mediaQueries.tablet} {
    min-width: 4.5rem;
  }
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Chip = styled.label`
  position: relative;
  margin: 0;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    display: inline-block;
    padding: 0.4rem 0.9rem;
    border: 1px solid ${borderColor};
    border-radius: 999px;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    background-color: ${({ theme }) => theme.body};
    color: ${({ theme }) => theme.text};
    transition: all 0.15s ease-in-out;
  }

  &:hover span {
    border-color: ${({ theme }) => theme.primaryOne};
  }

  input:focus-visible + span {
    outline: 2px solid ${({ theme }) => theme.primaryOne};
    outline-offset: 2px;
  }

  input:checked + span {
    background-color: ${({ theme }) => theme.primaryOne};
    border-color: ${({ theme }) => theme.primaryOne};
    color: #ffffff;
  }
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-start;

  ${mediaQueries.tablet} {
    justify-content: flex-end;
  }
`;

const ViewButton = styled(Button)`
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 1rem;
`;

const SORT_ACCESSORS = {
  createdAt: DATE_ACCESSOR("createdAt"),
  type: TEXT_ACCESSOR("type"),
  dedicaceFrom: TEXT_ACCESSOR("dedicaceFrom"),
  dedicaceTo: TEXT_ACCESSOR("dedicaceTo"),
  orderReason: TEXT_ACCESSOR("orderReason"),
  deliveryStatus: STATUS_ACCESSOR("deliveryStatus"),
};

const COLUMNS = [
  { key: "createdAt", label: "Date" },
  { key: "type", label: "Type" },
  { key: "dedicaceFrom", label: "Demandeur" },
  { key: "dedicaceTo", label: "Bénéficiaire" },
  { key: "orderReason", label: "Raison" },
  { key: "deliveryStatus", label: "Statut" },
];

const DisplayOrders = ({ orders }) => {
  const {
    sortedRows: sortedOrders,
    sort,
    toggleSort,
  } = useTableSort(orders, SORT_ACCESSORS);

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <SortableHeaders
            columns={COLUMNS}
            sort={sort}
            onSort={toggleSort}
          >
            <th scope={"col"}>Actions</th>
          </SortableHeaders>
        </thead>
        <tbody>
          {!sortedOrders || sortedOrders.length === 0 ? (
            <tr>
              <td colSpan={7}>
                <EmptyState>Aucune information disponible.</EmptyState>
              </td>
            </tr>
          ) : (
            sortedOrders.map((order, index) => (
              <tr key={order.id || index}>
                <td data-label={"Date"}>
                  {formatDate(new Date(order.createdAt), "Y-MM-dd")}
                </td>
                <td data-label={"Type"}>{order.type}</td>
                <td data-label={"Demandeur"}>{order.dedicaceFrom}</td>
                <td data-label={"Bénéficiaire"}>{order.dedicaceTo}</td>
                <td data-label={"Raison"}>{order.orderReason}</td>
                <td data-label={"Statut"}>
                  <OrderStatus status={order.deliveryStatus} />
                </td>
                <td data-label={"Actions"}>
                  {(order.deliveryStatus === "in_progress" ||
                    order.deliveryStatus === "processed" ||
                    order.deliveryStatus === "rejected") && (
                    <ViewButton
                      tag={Link}
                      color="primary"
                      to={"/admin/order"}
                      state={{ order: order }}
                      size={"sm"}
                    >
                      Voir
                    </ViewButton>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

const FilterChip = ({ id, name, value, label, defaultChecked, onChange }) => (
  <Chip htmlFor={id}>
    <input
      id={id}
      type={"radio"}
      name={name}
      value={value}
      defaultChecked={defaultChecked}
      onChange={onChange}
    />
    <span>{label}</span>
  </Chip>
);

const Orders = ({ orders, ordersLoading, ordersError }) => {
  const [inProgressOrders, setInProgressOrders] = useState();
  const [processedOrders, setProcessedOrders] = useState();
  const [rejectedOrders, setRejectedOrders] = useState();
  const [filters, setFilters] = useState({ status: null, type: null });
  const { celebrities } = useCelebritiesStore();

  useEffect(() => {
    if (orders) {
      setInProgressOrders(
        orders.filter((order) => order.deliveryStatus === "in_progress")
      );
      setProcessedOrders(
        orders.filter(
          (order) =>
            order.deliveryStatus === "processed" ||
            order.deliveryStatus === "accepted" ||
            order.deliveryStatus === "delivered"
        )
      );
      setRejectedOrders(
        orders.filter((order) => order.deliveryStatus === "rejected")
      );
    }
  }, [orders]);

  const onFiltersChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.checked ? e.target.value : null,
    });
  };

  const filterOrders = (e, baseOrders) => {
    e.preventDefault();

    const ordersToFilter = orders.filter(
      (order) =>
        order.deliveryStatus === "processed" ||
        order.deliveryStatus === "accepted" ||
        order.deliveryStatus === "delivered"
    );

    if (!filters.type && !filters.status) {
      setProcessedOrders(ordersToFilter);
      return;
    }
    if (!filters.type && filters.status) {
      setProcessedOrders(
        ordersToFilter.filter(
          (order) => order.deliveryStatus === filters.status
        )
      );
      return;
    }
    if (filters.type && !filters.status) {
      setProcessedOrders(
        ordersToFilter.filter((order) => order.type === filters.type)
      );
      return;
    }
    setProcessedOrders(
      ordersToFilter.filter(
        (order) =>
          order.type === filters.type && order.deliveryStatus === filters.status
      )
    );
  };

  const filterInProgressOrdersByType = (e) => {
    if (e.target.value === "all") {
      setInProgressOrders(
        orders.filter((order) => order.deliveryStatus === "in_progress")
      );
      return;
    }
    setInProgressOrders(
      orders.filter(
        (order) =>
          order.type === e.target.value &&
          order.deliveryStatus === "in_progress"
      )
    );
  };

  if (ordersLoading) {
    return (
      <Layout backPath={{ pathname: "/admin" }} celebrities={celebrities.items}>
        <Row className={"my-3"}>
          <Col sm={2} className="mx-auto">
            <Spinner color={"danger"} />
          </Col>
        </Row>
      </Layout>
    );
  }

  if (ordersError) {
    return (
      <Layout backPath={{ pathname: "/admin" }} celebrities={celebrities.items}>
        <Row className={"my-3"}>
          <Col sm={8} lg={6} className="mx-auto">
            <Alert color={"danger"}>
              Une erreur est survenue. Merci de réessayer plus tard.
            </Alert>
          </Col>
        </Row>
      </Layout>
    );
  }

  return (
    <Layout backPath={{ pathname: "/admin" }} celebrities={celebrities.items}>
      <Main>
        {orders.length === 0 ? (
          <div className={"my-3"}>Aucune commandes disponible.</div>
        ) : (
          <div className={"my-3"}>
            {rejectedOrders && rejectedOrders.length > 0 && (
              <Section>
                <SectionHeader>
                  <h2>Les livraisons rejetées (à refaire)</h2>
                  <CountBadge>{rejectedOrders.length}</CountBadge>
                </SectionHeader>
                <DisplayOrders orders={rejectedOrders} />
              </Section>
            )}

            <Section>
              <SectionHeader>
                <h2>Les commandes en attente de traitement</h2>
                <CountBadge>
                  {inProgressOrders ? inProgressOrders.length : 0}
                </CountBadge>
              </SectionHeader>

              <FilterForm>
                <FilterRow>
                  <FilterLegend>Type</FilterLegend>
                  <ChipGroup>
                    <FilterChip
                      id={"allType"}
                      name={"orderType"}
                      value={"all"}
                      label={"tous les types"}
                      defaultChecked
                      onChange={filterInProgressOrdersByType}
                    />
                    <FilterChip
                      id={"videoDedication"}
                      name={"orderType"}
                      value={"dédicace vidéo"}
                      label={"dédicace vidéo"}
                      onChange={filterInProgressOrdersByType}
                    />
                    <FilterChip
                      id={"musicalDedication"}
                      name={"orderType"}
                      value={"dédicace musicale"}
                      label={"dédicace musicale"}
                      onChange={filterInProgressOrdersByType}
                    />
                    <FilterChip
                      id={"videoBusiness"}
                      name={"orderType"}
                      value={"vidéo business"}
                      label={"vidéo business"}
                      onChange={filterInProgressOrdersByType}
                    />
                  </ChipGroup>
                </FilterRow>
              </FilterForm>

              <DisplayOrders orders={inProgressOrders} />
            </Section>

            <Section>
              <SectionHeader>
                <h2>Les commandes traitées</h2>
                <CountBadge>
                  {processedOrders ? processedOrders.length : 0}
                </CountBadge>
              </SectionHeader>

              <FilterForm>
                <FilterRow>
                  <FilterLegend>Type</FilterLegend>
                  <ChipGroup>
                    <FilterChip
                      id={"allType-2"}
                      name={"type"}
                      value={""}
                      label={"tous les types"}
                      onChange={onFiltersChange}
                    />
                    <FilterChip
                      id={"videoDedication-2"}
                      name={"type"}
                      value={"dédicace vidéo"}
                      label={"dédicace vidéo"}
                      onChange={onFiltersChange}
                    />
                    <FilterChip
                      id={"musicalDedication-2"}
                      name={"type"}
                      value={"dédicace musicale"}
                      label={"dédicace musicale"}
                      onChange={onFiltersChange}
                    />
                    <FilterChip
                      id={"videoBusiness-2"}
                      name={"type"}
                      value={"vidéo business"}
                      label={"vidéo business"}
                      onChange={onFiltersChange}
                    />
                  </ChipGroup>
                </FilterRow>

                <FilterRow>
                  <FilterLegend>Statut</FilterLegend>
                  <ChipGroup>
                    <FilterChip
                      id={"allStatus"}
                      name={"status"}
                      value={""}
                      label={"tous les statuts"}
                      onChange={onFiltersChange}
                    />
                    <FilterChip
                      id={"processed"}
                      name={"status"}
                      value={"processed"}
                      label={"traité"}
                      onChange={onFiltersChange}
                    />
                    <FilterChip
                      id={"accepted"}
                      name={"status"}
                      value={"accepted"}
                      label={"accepté"}
                      onChange={onFiltersChange}
                    />
                    <FilterChip
                      id={"delivered"}
                      name={"status"}
                      value={"delivered"}
                      label={"livré"}
                      onChange={onFiltersChange}
                    />
                  </ChipGroup>
                </FilterRow>

                <FilterActions>
                  <Button
                    color={"danger"}
                    size={"sm"}
                    className={"rounded-pill px-4 fw-semibold"}
                    onClick={(e) => filterOrders(e, inProgressOrders)}
                  >
                    Filtrer
                  </Button>
                </FilterActions>
              </FilterForm>

              <DisplayOrders orders={processedOrders} />
            </Section>
          </div>
        )}
      </Main>
    </Layout>
  );
};

export default Orders;
